/**
 * Value Edit Modal
 *
 * Modal dialog for creating and editing values.
 */

import { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import type { Value, CreateValueInput, UpdateValueInput, ValueCategory } from '@/types/value';
import { VALUE_CATEGORIES } from '@/types/value';

interface ValueEditModalProps {
  value?: Value | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (input: CreateValueInput | UpdateValueInput) => void;
  isLoading?: boolean;
}

export function ValueEditModal({
  value,
  isOpen,
  onClose,
  onSave,
  isLoading,
}: ValueEditModalProps) {
  const [formData, setFormData] = useState<CreateValueInput>({
    name: '',
    description: '',
    weight: 10,
    congruenceScore: 0.5,
    category: undefined,
    priority: undefined,
    evidenceOfLiving: '',
    evidenceOfViolating: '',
  });

  const isEditing = !!value;

  useEffect(() => {
    if (value) {
      setFormData({
        name: value.name,
        description: value.description || '',
        weight: value.weight,
        congruenceScore: value.congruenceScore,
        category: value.category,
        priority: value.priority,
        evidenceOfLiving: value.evidenceOfLiving || '',
        evidenceOfViolating: value.evidenceOfViolating || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        weight: 10,
        congruenceScore: 0.5,
        category: undefined,
        priority: undefined,
        evidenceOfLiving: '',
        evidenceOfViolating: '',
      });
    }
  }, [value, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="value-edit-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 bg-stone-50">
          <h2 className="text-xl font-display font-semibold text-stone-900" data-testid="value-edit-modal-title">
            {isEditing ? 'Edit Value' : 'Add New Value'}
          </h2>
          <button
            data-testid="cancel-button"
            onClick={onClose}
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Value Name <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="value-name-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Courage Over Comfort"
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                What this value means to you
              </label>
              <textarea
                data-testid="value-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this value means in your own words..."
                rows={2}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
              />
            </div>

            {/* Weight and Priority Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  Weight (1-100)
                </label>
                <input
                  data-testid="value-weight-input"
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 10 })}
                  min={1}
                  max={100}
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <p className="mt-1 text-xs text-stone-500">
                  Higher weight = larger slice in the Values Wheel
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  Priority Rank
                </label>
                <input
                  type="number"
                  value={formData.priority || ''}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || undefined })}
                  min={1}
                  max={20}
                  placeholder="e.g., 1"
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <p className="mt-1 text-xs text-stone-500">
                  1 = highest priority (for conflict resolution)
                </p>
              </div>
            </div>

            {/* Congruence Score */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Current Alignment Score: {Math.round(formData.congruenceScore! * 100)}%
              </label>
              <input
                type="range"
                value={formData.congruenceScore! * 100}
                onChange={(e) => setFormData({ ...formData, congruenceScore: parseInt(e.target.value) / 100 })}
                min={0}
                max={100}
                className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
              <div className="flex justify-between text-xs text-stone-500 mt-1">
                <span>Gap (0%)</span>
                <span>Partial (40%)</span>
                <span>Strong (70%)</span>
                <span>Aligned (100%)</span>
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Category (Schwartz)
              </label>
              <select
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: (e.target.value || undefined) as ValueCategory })}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="">Select category...</option>
                {Object.entries(VALUE_CATEGORIES).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Evidence of Living */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Evidence of Living This Value
              </label>
              <textarea
                value={formData.evidenceOfLiving}
                onChange={(e) => setFormData({ ...formData, evidenceOfLiving: e.target.value })}
                placeholder="What behaviors show you're living this value? e.g., Taking meds daily, exercising 3x/week..."
                rows={3}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
              />
            </div>

            {/* Evidence of Violating */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Warning Signs of Violating
              </label>
              <textarea
                value={formData.evidenceOfViolating}
                onChange={(e) => setFormData({ ...formData, evidenceOfViolating: e.target.value })}
                placeholder="What behaviors indicate you're not living this value? e.g., Skipping meds, poor food choices..."
                rows={3}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
            <button
              data-testid="cancel-button"
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-800 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="save-value-button"
              type="submit"
              disabled={isLoading || !formData.name}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-stone-300 rounded-lg transition-colors"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isEditing ? 'Save Changes' : 'Create Value'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

